import { getErrorMessage } from "@/lib/lutely-api/error-manager";
import { getAuthControllerGetCurrentUserQueryKey } from "@/lib/lutely-api/gen/auth/auth";
import {
  getCreatorsControllerDetailCreatorQueryKey,
  useCreatorsControllerUploadImage,
} from "@/lib/lutely-api/gen/creators/creators";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { toast } from "react-toastify";

type UseAvatarUploadOptions = {
  creatorId?: string;
  onSuccess?: () => void;
};

/**
 * Custom hook for handling avatar uploads with automatic cache invalidation
 * and theme regeneration triggers.
 */
export function useAvatarUpload({
  creatorId,
  onSuccess,
}: UseAvatarUploadOptions) {
  const queryClient = useQueryClient();
  const { mutateAsync: uploadProfilePicture, isPending: isUploading } =
    useCreatorsControllerUploadImage();

  const handleAvatarUpload = useCallback(
    async (file: File, refetchCreatorData: () => Promise<any>) => {
      if (!creatorId) {
        toast.error("Creator ID is required to upload profile picture.");
        return;
      }

      const loadingToast = toast.loading("Uploading Profile Picture...", {
        autoClose: 30000, // fallback timeout so the toast doesn't stay forever if the request hangs
      });

      try {
        await uploadProfilePicture({
          creatorId,
          imageType: "profile",
          data: { file },
        });

        // Small delay to allow server to process the upload
        await new Promise((resolve) => setTimeout(resolve, 300));

        // Invalidate and refetch creator query to get new avatar URL
        await queryClient.invalidateQueries({
          queryKey: getCreatorsControllerDetailCreatorQueryKey(creatorId),
        });
        await refetchCreatorData();

        // Invalidate and refetch user query to update avatar in nav bar
        await queryClient.invalidateQueries({
          queryKey: getAuthControllerGetCurrentUserQueryKey(),
        });
        await queryClient.refetchQueries({
          queryKey: getAuthControllerGetCurrentUserQueryKey(),
        });

        toast.update(loadingToast, {
          render: "Profile Picture Uploaded Successfully!",
          type: "success",
          isLoading: false,
          autoClose: 2000,
        });

        onSuccess?.();
      } catch (error: unknown) {
        toast.update(loadingToast, {
          render: getErrorMessage(error),
          type: "error",
          isLoading: false,
          autoClose: 3000,
        });
        throw error;
      }
    },
    [creatorId, uploadProfilePicture, queryClient, onSuccess],
  );

  return { handleAvatarUpload, isUploading };
}
